import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { PhaseType } from 'types/package';

import {
  STICKY_NAV_HEIGHT,
  getColorDarkBG,
  getListLabel,
  getColorLightBG,
  getListItemColor,
} from './styleHelpers';

export const StyledWrapper = styled.div`
  padding-top: 68px;
`;

export const StyledTopNav = styled.div<{
  variant: PhaseType;
  progress: number;
}>`
  position: fixed;
  z-index: 1;
  top: 0;
  height: ${STICKY_NAV_HEIGHT};
  width: 100%;
  background: ${({ theme }) => theme.colors.neutralColors.charcoal};
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  &:before {
    height: 4px;
    width: 100%;
    background-color: rgba(255, 255, 255, 0.25);
    backdrop-filter: blur(8px);
    content: '';
    display: block;
    position: absolute;
    bottom: -4px;
  }
  &:after {
    height: 4px;
    width: ${({ progress }) => progress}%;
    background-color: ${({ theme, variant }) => getColorDarkBG(theme, variant)};
    left: 0;
    content: '';
    display: block;
    position: absolute;
    bottom: -4px;
  }
  ${({ theme }) => theme.breakPoints.desktop} {
    cursor: auto;
  }
`;
export const StyledNavTitle = styled.p`
  color: ${({ theme }) => theme.colors.neutralColors.white};
  font-size: 1.4rem;
  text-align: center;
  margin: 0 80px;
  position: absolute;
  z-index: 1;
  svg {
    position: absolute;
    right: -20px;
    top: 50%;
    transform: translateY(-50%);
  }
  ${({ theme }) => theme.breakPoints.desktop} {
    display: block;
  }
`;

export const StyledNavTitleMobile = styled.p<{ menuOpen?: boolean }>`
  color: ${({ theme }) => theme.colors.neutralColors.white};
  font-size: 1.4rem;
  text-align: center;
  margin: 0 80px;
  position: absolute;
  z-index: 1;
  display: block;
  svg {
    position: absolute;
    right: -20px;
    top: 50%;
    transform: translateY(-50%)
      ${({ menuOpen }) => (menuOpen ? 'rotate(180deg)' : '')};
  }
  ${({ theme }) => theme.breakPoints.desktop} {
    display: none;
  }
`;

export const StyledMenuTray = styled(motion.div)`
  position: absolute;
  backdrop-filter: blur(8px);
  top: 100%;
  width: 100%;
  height: 100vh;
  z-index: -1;
  ${({ theme }) => theme.breakPoints.desktop} {
    display: none;
  }
`;
export const StyledCloseButton = styled(Link)<{ $menuOpen: boolean }>`
  position: absolute;
  right: 29px;
  top: 50%;
  transform: translateY(-50%);
  color: white;
  display: ${({ $menuOpen }) => ($menuOpen ? 'none' : 'block')};
  ${({ theme }) => theme.breakPoints.desktop} {
    display: block;
  }
`;

export const StyledTop = styled.div`
  margin: 0 auto;
  background: ${({ theme }) => theme.colors.neutralColors.charcoal};
  max-width: ${({ theme }) => theme.containerWidth};
  ${({ theme }) => theme.breakPoints.desktop} {
    display: flex;
    flex-direction: row-reverse;
    justify-content: center;
  }
  &:after {
    content: '';
    background: ${({ theme }) => theme.colors.neutralColors.charcoal};
    position: absolute;
    left: 0;
    top: ${STICKY_NAV_HEIGHT};
    z-index: -1;
    height: 540px;
    width: 100%;
  }
`;

export const StyledHero = styled.img`
  height: 320px;
  width: 100%;
  object-fit: cover;
  margin-bottom: 40px;
  ${({ theme }) => theme.breakPoints.desktop} {
    height: 540px;
    width: 540px;
    margin-bottom: 0;
  }
`;

export const TopContentWrapper = styled.div`
  padding: 0 20px;
  ${({ theme }) => theme.breakPoints.desktop} {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    width: 648px;
  }
`;

export const StyledHeading = styled.h1`
  font-size: 3.6rem;
  line-height: 4rem;
  font-family: 'Ivar Fine Light';
  color: ${({ theme }) => theme.colors.textColors.primaryLight};
  margin-bottom: 8px;
`;

export const StyledReadTime = styled.p`
  font-size: 1.2rem;
  line-height: 2.4rem;
  color: ${({ theme }) => theme.colors.brandColors.coral};
  font-family: 'Moderat';
  margin-bottom: 8px;
`;

export const StyledDescription = styled.p`
  font-size: 1.8rem;
  line-height: 2.8rem;
  color: ${({ theme }) => theme.colors.textColors.secondaryLight};
  font-family: 'Moderat';
  margin-bottom: 24px;
`;
export const StyledAuthorContainer = styled.div`
  margin-bottom: 32px;
`;
export const StyledAuthor = styled.p`
  font-size: 1.4rem;
  line-height: 2rem;
  color: ${({ theme }) => theme.colors.textColors.primaryLight};
  font-family: 'Moderat';
  margin-bottom: 8px;
`;

export const StyledAnchorMobileListWrapper = styled.div`
  ${({ theme }) => theme.breakPoints.desktop} {
    display: none;
  }
`;

export const StyledAnchorMobileListDropdownWrapper = styled.div`
  background: ${({ theme }) => theme.colors.neutralColors.darkerGray};
  padding: 32px 22px 44px;
  border-radius: 0 0 8px 8px;
  ${({ theme }) => theme.breakPoints.desktop} {
    display: none;
  }
`;

export const StyledAnchorDesktopListWrapper = styled.div<{
  variant: PhaseType;
}>`
  display: none;
  ${({ theme }) => theme.breakPoints.desktop} {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    position: sticky;
    top: 150px;
    margin-top: 40px;
    right: 0;
    align-self: flex-start;
    width: 540px;
    padding-left: 60px;
  }
`;

export const StyledListHeader = styled.p`
  font-size: 1.8rem;
  line-height: 2.4rem;
  color: ${({ theme }) => theme.colors.textColors.secondaryLight};
  font-family: 'Moderat';
  margin-bottom: 20px;
  ${({ theme }) => theme.breakPoints.desktop} {
    color: ${({ theme }) => theme.colors.textColors.secondaryDark};
  }
`;

export const StyledAnchorList = styled.ol`
  padding-bottom: 48px;
  list-style: none;
  font-size: 1.8rem;
  line-height: 2.4rem;
`;

export const SectionContentWrapper = styled.div`
  padding: 0 20px;
  ${({ theme }) => theme.breakPoints.desktop} {
    padding: 0;
    display: flex;
    justify-content: center;
    flex-direction: row-reverse;
    max-width: ${({ theme }) => theme.containerWidth};
    margin: 0 auto;
  }
`;

export const StyledSectionList = styled.ol`
  margin-top: 40px;
  list-style: none;
  font-size: 1.8rem;
  line-height: 2.4rem;
  padding-bottom: 140px;
  ${({ theme }) => theme.breakPoints.desktop} {
    width: 648px;
    padding-left: 20px;
  }
`;

export const StyledListItem = styled.li<{
  variant: PhaseType;
  index: number;
  isActiveSection?: boolean;
  isDropdownListItem?: boolean;
  isLightBG?: boolean;
}>`
  font-size: 1.8rem;
  margin-bottom: 20px;
  position: relative;
  padding-left: 28px;
  cursor: pointer;
  display: block;
  color: ${({ theme, isDropdownListItem, isActiveSection, isLightBG }) =>
    getListItemColor(theme, isDropdownListItem, isActiveSection, isLightBG)};
  &:last-child {
    margin-bottom: 0;
  }
  &:before {
    color: ${({ theme, variant, isLightBG }) =>
      isLightBG
        ? getColorLightBG(theme, variant)
        : getColorDarkBG(theme, variant)};
    content: '${({ index }) => getListLabel(index)}';
    font-family: 'Caveat';
    font-size: 2rem;
    margin-right: 8px;
    position: absolute;
    left: 0px;
  }
  &:after {
    content: '';
    position: absolute;
    display: ${({ isActiveSection }) => (isActiveSection ? 'block' : 'none')};
    left: -8px;
    top: 0;
    height: 100%;
    width: 2px;
    border-radius: 4px;
    background: ${({ theme, variant }) => getColorLightBG(theme, variant)};
  }
`;

export const StyledContentListItem = styled.li<{
  variant: PhaseType;
  index: number;
}>`
  font-size: 1.8rem;
  margin-bottom: 20px;
  position: relative;
  display: block;
  color: ${({ theme }) => theme.colors.textColors.primaryDark};
  &:last-child {
    margin-bottom: 0;
  }
  &:before {
    color: ${({ theme, variant }) => getColorLightBG(theme, variant)};
    content: '${({ index }) => getListLabel(index)}';
    font-family: 'Caveat';
    font-size: 2rem;
    margin-right: 8px;
    left: 0px;
  }
`;

export const StyledArticleHeader = styled.h2`
  font-size: 3.2rem;
  line-height: 3.6rem;
  color: ${({ theme }) => theme.colors.textColors.primaryDark};
  font-family: 'Ivar Fine Light';
  margin-bottom: 32px;
`;

export const StyledArticleContent = styled.section`
  h3 {
    font-size: 2rem;
    line-height: 2.4rem;
    color: ${({ theme }) => theme.colors.textColors.primaryDark};
    font-family: 'Moderat';
    margin-bottom: 24px;
  }
  h6 {
    font-size: 2rem;
    line-height: 2.4rem;
    color: ${({ theme }) => theme.colors.textColors.primaryDark};
    font-family: 'Moderat';
    margin-bottom: 8px;
  }
  p,
  span {
    font-size: 1.6rem;
    line-height: 2.4rem;
    color: ${({ theme }) => theme.colors.textColors.secondaryDark};
  }
  span {
    margin-bottom: 16px;
  }
  img {
    display: block;
    margin: 16px auto;
    /* Had to use a reverse breakpoint so the image would revert to attr: width on desktop */
    @media screen and (max-width: 1025px) {
      width: 100%;
    }
  }
  ol,
  ul {
    padding-left: 20px;
  }
  li {
    font-size: 1.6rem;
    line-height: 2.4rem;
    color: ${({ theme }) => theme.colors.textColors.primaryDark};
    list-style-type: disc;
    margin: 16px 0;
    &::marker {
      color: ${({ theme }) => theme.colors.textColors.tertiaryDark};
    }
  }
  b {
    font-family: 'Moderat Bold';
  }
  table {
    table-layout: fixed;
    width: 100%;
  }
  th {
    background: ${({ theme }) => theme.colors.neutralColors.lightestGray};
    padding: 16px;
    color: ${({ theme }) => theme.colors.textColors.primaryDark};
    font-size: 1.6rem;
  }
  td {
    overflow-wrap: break-word;
    color: ${({ theme }) => theme.colors.textColors.secondaryDark};
    font-size: 1.4rem;
    border-right: 2px solid
      ${({ theme }) => theme.colors.neutralColors.lighterGray};
    background: ${({ theme }) => theme.colors.neutralColors.concrete};
    padding: 16px;
    vertical-align: top;
    &:last-child {
      border-right: 0;
    }
    span {
      margin-bottom: 8px;
    }
  }
`;
